/**
 * Chat Service
 * Handles AI chat-related API calls by matching queries to dashboard APIs
 */

import { dashboardService } from "./dashboard.service";
import { toonService } from "./toon.service";
import { callChatGPT, callChatGPTGeneral } from "./chatgpt.service";
import { logUserPrompt, logAIResponse } from "./chat-log.service";
import type { SendChatMessageRequest, SendChatMessageResponse } from "../types";

// Query matching configuration
interface DashboardQueryMatch {
  keywords: string[];
  apiCall: (firmId?: number) => Promise<any>;
  name: string;
  errorMessage: string;
}

/**
 * Match user query to dashboard API with comprehensive keyword filtering
 * Checks are ordered from most specific to least specific to avoid conflicts
 */
function matchQueryToDashboard(query: string): DashboardQueryMatch | null {
  const normalizedMessage = query.toLowerCase().trim();

  // DASHBOARD ONLINE (check first as it's more specific)
  if (
    normalizedMessage.includes("dashboard online") ||
    normalizedMessage.includes("online sales") ||
    normalizedMessage.includes("online collections") ||
    normalizedMessage.includes("online orders") ||
    (normalizedMessage.includes("online") && !normalizedMessage.includes("outstanding"))
  ) {
    return {
      keywords: [],
      apiCall: (firmId) => dashboardService.getOnlineDashboard({ firmId }),
      name: "Online Dashboard",
      errorMessage: "I could not load the online summary data. Please try again or open the Online dashboard screen directly.",
    };
  }

  // DASHBOARD OUTSTANDING (check before general "outstanding")
  if (
    normalizedMessage.includes("dashboard outstanding") ||
    normalizedMessage.includes("outstanding payments") ||
    normalizedMessage.includes("outstanding collections") ||
    normalizedMessage.includes("outstanding orders") ||
    normalizedMessage.includes("outstanding sales") ||
    normalizedMessage.includes("outstanding inventory") ||
    normalizedMessage.includes("outstanding purchases") ||
    normalizedMessage.includes("risk") ||
    normalizedMessage.includes("range") ||
    normalizedMessage.includes("outstanding")
  ) {
    return {
      keywords: [],
      apiCall: (firmId) => dashboardService.getOutstandingDashboard({ firmId }),
      name: "Outstanding Dashboard",
      errorMessage: "I was not able to load dashboard outstanding information right now.",
    };
  }

  // DASHBOARD SALESMAN (check before general "sales")
  if (
    normalizedMessage.includes("salesman performance") ||
    normalizedMessage.includes("salesman wise") ||
    normalizedMessage.includes("salesman analysis") ||
    normalizedMessage.includes("salesman")
  ) {
    return {
      keywords: [],
      apiCall: (firmId) => dashboardService.getSalesmanAnalysis({ firmId }),
      name: "Salesman Analysis",
      errorMessage: "I was not able to load salesman analysis right now.",
    };
  }

  // DASHBOARD EXPIRES (check before general "expired" in summary)
  if (
    normalizedMessage.includes("dashboard expires") ||
    normalizedMessage.includes("expires") ||
    normalizedMessage.includes("expiry") ||
    normalizedMessage.includes("expiring")
  ) {
    return {
      keywords: [],
      apiCall: (firmId) => dashboardService.getExpires({ firmId }),
      name: "Dashboard Expires",
      errorMessage: "I was not able to load expiry information right now. Please try again or open the Dashboard screen directly.",
    };
  }

  // DASHBOARD SALES & PURCHASES (check before general "sales" in summary)
  if (
    normalizedMessage.includes("sales and purchase") ||
    normalizedMessage.includes("sales & purchase") ||
    normalizedMessage.includes("sales purchase") ||
    normalizedMessage.includes("sales performance") ||
    normalizedMessage.includes("increase sales") ||
    (normalizedMessage.includes("purchase") && normalizedMessage.includes("year")) ||
    (normalizedMessage.includes("sales") && (normalizedMessage.includes("purchase") || normalizedMessage.includes("year")))
  ) {
    return {
      keywords: [],
      apiCall: (firmId) => dashboardService.getSalesPurchases({ firmId }),
      name: "Sales & Purchases",
      errorMessage: "I was not able to load sales and purchase data right now. Please try again or open the Sales dashboard screen directly.",
    };
  }

  // DASHBOARD SUMMARY (catch-all for dashboard-related queries)
  if (
    normalizedMessage.includes("dashboard") ||
    normalizedMessage.includes("summary") ||
    normalizedMessage.includes("overview") ||
    normalizedMessage.includes("metrics") ||
    normalizedMessage.includes("analytics") ||
    normalizedMessage.includes("kpis") ||
    normalizedMessage.includes("key performance indicators") ||
    normalizedMessage.includes("performance") ||
    normalizedMessage.includes("growth") ||
    normalizedMessage.includes("rate") ||
    normalizedMessage.includes("percentage") ||
    normalizedMessage.includes("items") ||
    normalizedMessage.includes("expired") ||
    normalizedMessage.includes("inventory") ||
    normalizedMessage.includes("stock") ||
    normalizedMessage.includes("expiry") ||
    normalizedMessage.includes("value") ||
    normalizedMessage.includes("previous") ||
    normalizedMessage.includes("current") ||
    normalizedMessage.includes("sales") ||
    normalizedMessage.includes("purchase")
  ) {
    return {
      keywords: [],
      apiCall: (firmId) => dashboardService.getDashboardSummary({ firmId }),
      name: "Dashboard Summary",
      errorMessage: "I was not able to load the dashboard summary. Please open the Dashboard screen to check the latest data.",
    };
  }

  // If no screen-related keyword detected, return null
  return null;
}

export const chatService = {
  /**
   * Send a message to the AI chatbot
   * Matches queries to dashboard APIs and returns JSON response
   */
  async sendMessage(params: SendChatMessageRequest): Promise<SendChatMessageResponse> {
    const { message, firmId } = params;
    
    // Log user prompt
    logUserPrompt(message, params.conversationId, firmId);
    
    // Match query to dashboard API
    const matchedQuery = matchQueryToDashboard(message);
    
    if (matchedQuery) {
      try {
        // Call the matched dashboard API
        const data = await matchedQuery.apiCall(firmId);
        
        // Convert JSON to TOON format
        const toonResponse = toonService.jsonToToon(data);
        
        // Send user query and TOON data to ChatGPT
        const chatGPTResult = await callChatGPT(
          message,
          toonResponse,
          matchedQuery.name
        );
        
        // Log AI response
        logAIResponse(message, chatGPTResult.response, {
          dashboardName: matchedQuery.name,
          toonData: toonResponse,
          conversationId: params.conversationId,
          firmId,
        });
        
        // Return ChatGPT's LLM response (not TOON data)
        return {
          message: {
            id: `ai-${Date.now()}`,
            role: "assistant",
            content: chatGPTResult.response,
            timestamp: new Date().toISOString(),
            relevantQuestions: chatGPTResult.relevantQuestions,
          },
          conversationId: params.conversationId,
          success: true,
          relevantQuestions: chatGPTResult.relevantQuestions,
        };
      } catch (error: any) {
        // Handle API errors with custom error message
        const errorMessage = matchedQuery.errorMessage || `Error: ${error?.message || "Failed to fetch dashboard data"}`;
        
        // Log error response
        logAIResponse(message, errorMessage, {
          conversationId: params.conversationId,
          firmId,
        });
        
        return {
          message: {
            id: `error-${Date.now()}`,
            role: "assistant",
            content: errorMessage,
            timestamp: new Date().toISOString(),
          },
          conversationId: params.conversationId,
          success: false,
        };
      }
    }
    
    // If no match found, send to ChatGPT for general Pharmasoft-related questions
    try {
      const chatGPTResult = await callChatGPTGeneral(message);
      
      // Log AI response
      logAIResponse(message, chatGPTResult.response, {
        conversationId: params.conversationId,
        firmId,
      });
      
      return {
        message: {
          id: `ai-${Date.now()}`,
          role: "assistant",
          content: chatGPTResult.response,
          timestamp: new Date().toISOString(),
          relevantQuestions: chatGPTResult.relevantQuestions,
        },
        conversationId: params.conversationId,
        success: true,
        relevantQuestions: chatGPTResult.relevantQuestions,
      };
    } catch (error: any) {
      // Handle ChatGPT API errors
      const errorMessage = `I apologize, but I'm having trouble processing your request right now. Please try again later.`;
      
      // Log error response
      logAIResponse(message, errorMessage, {
        conversationId: params.conversationId,
        firmId,
      });
      
      return {
        message: {
          id: `error-${Date.now()}`,
          role: "assistant",
          content: errorMessage,
          timestamp: new Date().toISOString(),
        },
        conversationId: params.conversationId,
        success: false,
      };
    }
  },
};
